import { Icon } from '../common/Icon'
import { SiteSelect } from './SiteSelect'
import { cn } from '../../lib/cn'

interface Option {
  value: string
  label: string
}

interface FieldProps {
  as?: 'input' | 'textarea' | 'select'
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  required?: boolean
  type?: string
  placeholder?: string
  rows?: number
  autoComplete?: string
  options?: Option[]
  hint?: string
}

export function Field({
  as = 'input',
  id,
  label,
  value,
  onChange,
  error,
  required,
  type = 'text',
  placeholder,
  rows = 5,
  autoComplete,
  options,
  hint,
}: FieldProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  const base = cn(
    'w-full rounded-lg border bg-white px-4 py-3 text-[0.95rem] text-ink outline-none transition-colors placeholder:text-ink-300 focus:ring-2 focus:ring-lime-bright',
    error ? 'border-red-400' : 'border-sand-200 focus:border-brand-400',
  )

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {as === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          required={required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={cn(base, 'resize-y')}
        />
      ) : as === 'select' ? (
        <SiteSelect
          id={id}
          value={value}
          onChange={onChange}
          options={options ?? []}
          placeholder={placeholder}
          error={!!error}
          ariaDescribedBy={describedBy}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={base}
        />
      )}

      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-ink-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600">
          <Icon name="AlertCircle" className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  )
}
